import React, { useState } from 'react';
import { Truck, MapPin, Clock, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShippingEstimator = ({ productId, quantity = 1 }) => {
  const [postalCode, setPostalCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleEstimate = async (e) => {
    e.preventDefault();
    if (!postalCode.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/api/shipping/estimate`, {
        product_id: productId,
        postal_code: postalCode,
        quantity: quantity
      });
      setEstimate(res.data);
      setIsExpanded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to estimate shipping. Please check your postal code.');
      setEstimate(null);
    } finally {
      setLoading(false);
    }
  };

  const getRegionName = (region) => {
    switch (region) {
      case 'US': return 'United States';
      case 'CA': return 'Canada';
      case 'INTL': return 'International';
      default: return region;
    }
  };

  return (
    <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200 overflow-hidden" data-testid="shipping-estimator">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-100/50 transition-colors"
        data-testid="shipping-estimator-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#003456] rounded-lg flex items-center justify-center">
            <Truck className="h-5 w-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-[#0a0203]">Shipping Estimator</h3>
            <p className="text-sm text-gray-500">Calculate delivery cost and time</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-200 pt-4">
          <form onSubmit={handleEstimate} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="Enter ZIP or Postal Code"
                className="pl-10"
                data-testid="shipping-postal-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading || !postalCode.trim()}
              className="bg-[#003456] hover:bg-[#002B47]"
              data-testid="shipping-estimate-btn"
            >
              {loading ? 'Calculating...' : 'Estimate'}
            </Button>
          </form>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {estimate && (
            <div className="space-y-4 animate-fade-in">
              {/* Region info */}
              <div className="flex items-center justify-between text-sm text-gray-600 pb-3 border-b border-slate-200">
                <span>Shipping to: <strong>{getRegionName(estimate.region)}</strong></span>
                <span>Est. Weight: <strong>{estimate.total_weight} lbs</strong></span>
              </div>

              {/* Message if any */}
              {estimate.message && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>{estimate.message}</span>
                </div>
              )}

              {/* Shipping options */}
              <div className="space-y-2">
                {estimate.options.map((option, idx) => (
                  <div 
                    key={option.method}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      idx === 0 
                        ? 'border-[#003456] bg-[#003456]/5' 
                        : 'border-gray-200 bg-white'
                    }`}
                    data-testid={`shipping-option-${option.method}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100">
                        <Truck className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0a0203]">{option.name}</p>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="h-3 w-3" />
                          <span>
                            {option.days_min === option.days_max 
                              ? `${option.days_min} business day${option.days_min > 1 ? 's' : ''}`
                              : `${option.days_min}-${option.days_max} business days`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-[#0a0203]" style={{ fontFamily: 'JetBrains Mono' }}>
                        ${option.cost.toFixed(2)}
                      </span>
                      {idx === 0 && (
                        <p className="text-xs text-[#003456] font-medium">Best Value</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-500 text-center">
                Shipping rates are estimates based on weight. Final cost calculated at checkout.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShippingEstimator;
